import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { FC, MouseEvent, useState } from "react";
import { AnimatePresence } from "framer-motion";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { Td } from "@/components/Table";
import { MinimalChannelResource } from "@/types/resources";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import Text from "@/components/Text";
import WatermarkForm from "./WatermarkForm";

const Files = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

const FileBox = styled.div`
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  > svg {
    color: ${({ theme }) => theme.colors.gray};
    margin-right: 10px;
  }
  > ${Text} {
    flex: 1;
  }
`;

const FileActions = styled.div`
  margin-left: 12px;
  display: flex;
  gap: 4px;
`;

const FileActionButton = styled.button`
  display: flex;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  background: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  transition: 0.2s ease-out border-color;

  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ChannelIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

interface Props {
  channel: MinimalChannelResource;
}

const WatermarkRow: FC<Props> = ({ channel }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const [showEditForm, setShowEditForm] = useState(false);

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete watermark for this channel?",
        channel.name
      );

      if (!confirmed) return;

      await api.delete(`/v1/channels/${channel.id}/watermark`);

      queryClient.invalidateQueries(["watermarks"], {
        exact: false,
      });

      alerts.success("Watermark successfully deleted");
    } catch (_) {
      alerts.error();
    }
  };

  const onEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowEditForm(true);
  };

  const { watermarks } = channel;

  return (
    <tr>
      <Td>
        <Name>
          {!!channel.thumbnail && (
            <ChannelIcon src={channel.thumbnail} alt={`${channel.name} icon`} />
          )}
          <Text as="span" $type="smallHeadline">
            {channel.name}
          </Text>
        </Name>
      </Td>
      <Td>
        <Files>
          {watermarks?.map((media) => (
            <div key={media.id}>
              <Text $type="smallHeadline">{media.meta!.ratio} ratio</Text>
              <FileBox>
                <svg
                  width="15"
                  height="16"
                  viewBox="0 0 15 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.2819 4.57091L4.33139 10.6203C4.11706 10.8346 3.99665 11.1253 3.99665 11.4284C3.99665 11.7315 4.11706 12.0222 4.33139 12.2365C4.54571 12.4508 4.83641 12.5713 5.13951 12.5713C5.44261 12.5713 5.7333 12.4508 5.94763 12.2365L13.0411 5.04429C13.2533 4.83204 13.4217 4.58007 13.5365 4.30275C13.6514 4.02544 13.7105 3.72821 13.7105 3.42805C13.7105 3.12788 13.6514 2.83066 13.5365 2.55334C13.4217 2.27603 13.2533 2.02405 13.041 1.8118C12.8288 1.59956 12.5768 1.43119 12.2995 1.31632C12.0222 1.20146 11.725 1.14233 11.4248 1.14233C11.1246 1.14233 10.8274 1.20146 10.5501 1.31632C10.2728 1.43119 10.0208 1.59956 9.80856 1.8118L2.71514 9.00403C2.07216 9.64701 1.71094 10.5191 1.71094 11.4284C1.71094 12.3377 2.07216 13.2098 2.71514 13.8528C3.35812 14.4957 4.2302 14.857 5.13951 14.857C6.04882 14.857 6.92089 14.4957 7.56388 13.8528L13.4248 7.99948"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Text $type="smallNormal">{media.name}</Text>
                <FileActions>
                  <FileActionButton
                    as="a"
                    href={media.versions.original}
                    download
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 6.78571L6 10.7857L10 6.78571"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 1.64282V10.7857"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </FileActionButton>
                  <FileActionButton
                    as="a"
                    href={media.versions.original}
                    target="_blank"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.4375 9.375C7.61212 9.375 9.375 7.61212 9.375 5.4375C9.375 3.26288 7.61212 1.5 5.4375 1.5C3.26288 1.5 1.5 3.26288 1.5 5.4375C1.5 7.61212 3.26288 9.375 5.4375 9.375Z"
                        stroke="#222222"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.2207 8.22192L10.4989 10.5001"
                        stroke="#222222"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </FileActionButton>
                </FileActions>
              </FileBox>
            </div>
          ))}
        </Files>
      </Td>
      <Td>
        <ActionsDropdown>
          <ActionsDropdownButton as="button" onClick={onEdit}>
            <PencilIcon />
            Edit
          </ActionsDropdownButton>
          <ActionsDropdownButton as="button" onClick={onDelete}>
            <TrashIcon />
            Delete
          </ActionsDropdownButton>
        </ActionsDropdown>
        <AnimatePresence>
          {showEditForm && (
            <WatermarkForm
              editableChannel={channel}
              onClose={() => setShowEditForm(false)}
            />
          )}
        </AnimatePresence>
      </Td>
    </tr>
  );
};

export default WatermarkRow;
